import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, Calendar, DollarSign, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SalarySlipsPage() {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/salary-slips/list`, { withCredentials: true })
      .then(({ data }) => { setSlips(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const openSlip = (month) => {
    window.open(`${API}/salary-slips?month=${encodeURIComponent(month)}`, '_blank');
  };

  const downloadPdf = (month) => {
    // Trigger authenticated PDF download
    const link = document.createElement('a');
    link.href = `${API}/salary-slips/download?month=${encodeURIComponent(month)}`;
    link.setAttribute('download', `Salary_Slip_${month.replace(/\s+/g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openAll = () => {
    window.open(`${API}/salary-slips`, '_blank');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <p className="text-sm text-[#5C5C55]">Loading salary slips...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl" data-testid="salary-slips-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1C19] tracking-tight">Salary Slips</h1>
          <p className="text-sm text-[#5C5C55] mt-1">Kinntegraa L.L.C-FZ — Monthly payslips</p>
        </div>
        <button
          data-testid="download-all-slips"
          onClick={openAll}
          className="flex items-center gap-2 px-4 py-2 bg-[#284A33] text-white text-sm font-medium rounded-md hover:bg-[#1a3a24] transition-colors"
        >
          <ExternalLink size={14} />
          View All Slips
        </button>
      </div>

      <div className="grid gap-3">
        {[...slips].reverse().map((slip) => (
          <div
            key={slip.month}
            data-testid={`slip-${slip.month.replace(/\s+/g, '-').toLowerCase()}`}
            className="bg-white border border-[#E2E0D8] rounded-lg p-4 flex items-center justify-between hover:border-[#284A33]/30 hover:shadow-sm transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#284A33]/8 flex items-center justify-center shrink-0 group-hover:bg-[#284A33]/15 transition-colors">
                <FileText size={18} className="text-[#284A33]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#1C1C19]">{slip.month}</h3>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-[#5C5C55] flex items-center gap-1">
                    <Calendar size={11} />
                    {slip.period}
                  </span>
                  <span className="text-xs text-[#5C5C55]">Paid: {slip.pay_date}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right mr-2">
                <p className="text-sm font-bold text-[#1C1C19] font-mono">
                  AED {slip.net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-[10px] text-[#5C5C55] uppercase tracking-wide">Net Pay</p>
              </div>
              <button
                data-testid={`view-${slip.month.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => openSlip(slip.month)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F7F6F2] border border-[#E2E0D8] text-[#1C1C19] text-xs font-medium rounded-md hover:bg-[#eeedea] transition-all"
                title="View in browser"
              >
                <ExternalLink size={13} />
                View
              </button>
              <button
                data-testid={`download-${slip.month.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => downloadPdf(slip.month)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#284A33] border border-[#284A33] text-white text-xs font-medium rounded-md hover:bg-[#1a3a24] transition-all"
                title="Download PDF"
              >
                <Download size={13} />
                PDF
              </button>
            </div>
          </div>
        ))}
      </div>

      {slips.length === 0 && (
        <div className="text-center py-16 text-[#5C5C55]">
          <FileText size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No salary slips available</p>
        </div>
      )}

      <div className="mt-6 p-3 bg-[#F7F6F2] rounded-lg border border-[#E2E0D8]">
        <p className="text-[11px] text-[#5C5C55]">
          <strong>Tip:</strong> Click "Download" to open the salary slip in a new tab. Use <strong>Ctrl+P</strong> (or <strong>Cmd+P</strong> on Mac) to save as PDF. Enable "Background graphics" in the print dialog to preserve colors.
        </p>
      </div>
    </div>
  );
}
